import { useEffect, useMemo, useState } from 'react'
import { IOpeningHour } from '@/api/restaurants/types/restaurant-types'
import { parseDayOfWeek } from '@/features/restaurants/utils/parse-day-of-week'

export interface IRestaurantOpeningStatus {
    isOpen: boolean
    /** "23:00", set when open */
    closingTime?: string
    /** "18:00", set when closed but opening again later today */
    nextOpeningTime?: string
}

const toFloat = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number)

    return hours + (minutes || 0) / 60
}

/**
 * Whether the restaurant is currently open plus the relevant absolute time
 * (closing time when open, next opening time when closed). Returns null until
 * mounted, the calculation depends on the visitor's local clock and would
 * otherwise mismatch on hydration. Recalculates every minute.
 */
export const useRestaurantOpeningStatus = (
    openingHours: IOpeningHour[] | undefined
): IRestaurantOpeningStatus | null => {
    const [nowTick, setNowTick] = useState<number | null>(null)

    useEffect(() => {
        setNowTick(Date.now())

        const interval = setInterval(() => setNowTick(Date.now()), 1000 * 60)

        return () => clearInterval(interval)
    }, [])

    return useMemo(() => {
        if (nowTick === null || !openingHours?.length) return null

        const now = new Date(nowTick)
        const day = parseDayOfWeek(now.getDay())
        const nowTime = now.getHours() + now.getMinutes() / 60

        const todaysOpeningHours = openingHours.filter((openingHour) => openingHour.day === day)

        const currentOpeningHour = todaysOpeningHours.find(({ openingTime, closingTime }) => {
            const open = toFloat(openingTime)
            const close = toFloat(closingTime) || 24

            // Some opening hours go from e.g. 10:00 to 02:00 the next day
            const isOpenOvernight = close < open

            return nowTime >= open && (nowTime <= close || isOpenOvernight)
        })

        if (currentOpeningHour) {
            return { isOpen: true, closingTime: currentOpeningHour.closingTime }
        }

        const nextOpeningHour = todaysOpeningHours
            .filter(({ openingTime }) => toFloat(openingTime) > nowTime)
            .sort((a, b) => toFloat(a.openingTime) - toFloat(b.openingTime))[0]

        return { isOpen: false, nextOpeningTime: nextOpeningHour?.openingTime }
    }, [nowTick, openingHours])
}
